import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Search, BadgeCheck, KeyRound, X, FileText } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import SiteNav from "@/components/SiteNav";
import MarketTabs from "@/components/MarketTabs";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import { ENGINES, PROJECT_TYPES, CLEARANCE_COLORS } from "@/content/engines";

const EASE = [0.16, 1, 0.3, 1];
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

function HireModal({ person, onClose }) {
  const navigate = useNavigate();
  const engines = person.engines.length ? person.engines : ["ue5"];
  const [engine, setEngine] = useState(engines[0]);
  const [version, setVersion] = useState("");
  const [frameworks, setFrameworks] = useState([]);
  const [brief, setBrief] = useState("");
  const [quote, setQuote] = useState("");
  const [busy, setBusy] = useState(false);
  const eng = ENGINES[engine];

  const toggleFw = (f) => setFrameworks((arr) => (arr.includes(f) ? arr.filter((x) => x !== f) : [...arr, f]));

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/market/hire", { dev_id: person.id, brief, quote_amount: parseFloat(quote), engine, engine_version: version, frameworks });
      toast.success("Clearance granted — Command Center opened");
      navigate(`/command?engagement=${r.data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="hire-modal" className="border border-white/15 bg-[#0a0a0a] max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase">Hire // {person.name}</span>
          <button data-testid="hire-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 flex flex-col gap-4">
          <div>
            <label className={labelCls}>Target platform *</label>
            <div className="grid grid-cols-2 gap-2">
              {engines.map((e) => (
                <button key={e} data-testid={`hire-engine-${e}`} onClick={() => { setEngine(e); setFrameworks([]); }} className={`border px-3 py-2.5 font-mono text-[10px] tracking-[0.15em] uppercase text-left transition-colors ${engine === e ? ENGINES[e].accent : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                  {ENGINES[e].label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Engine version</label>
              <select data-testid="hire-version" className={inputCls} value={version} onChange={(e) => setVersion(e.target.value)}>
                <option value="">Select…</option>
                {eng.versions.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Quote (AUD) *</label>
              <input data-testid="hire-quote" type="number" min="1" className={inputCls} placeholder="e.g. 500" value={quote} onChange={(e) => setQuote(e.target.value)} />
            </div>
          </div>
          {eng.frameworks.length > 0 && (
            <div>
              <label className={labelCls}>Required frameworks</label>
              <div className="flex gap-2 flex-wrap">
                {eng.frameworks.map((f) => (
                  <button key={f} data-testid={`hire-fw-${f.replace(/[^A-Za-z]/g, "")}`} onClick={() => toggleFw(f)} className={`border px-2.5 py-1.5 font-mono text-[9px] tracking-[0.15em] uppercase transition-colors ${frameworks.includes(f) ? "text-nc-orange border-nc-orange/50 bg-nc-orange/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>{f}</button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className={labelCls}>Mission brief * (min 10 chars)</label>
            <textarea data-testid="hire-brief" rows={4} className={`${inputCls} resize-none`} placeholder="What do you need built, installed or fixed?" value={brief} onChange={(e) => setBrief(e.target.value)} />
          </div>
          <p className="font-mono text-[9px] text-zinc-600 leading-relaxed">
            Funds are held in platform escrow and only released after you verify the work — minus the platform fee. A service fee applies at checkout.
          </p>
          <button data-testid="hire-submit" disabled={busy || brief.trim().length < 10 || !(parseFloat(quote) > 0)} onClick={submit} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-5 py-3 flex items-center justify-center gap-2 transition-colors">
            <KeyRound className="w-4 h-4" /> {busy ? "Processing…" : "Confirm Hire & Open Project"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GalleryPage() {
  const navigate = useNavigate();
  const { fmt } = useCurrency();
  const [personnel, setPersonnel] = useState([]);
  const [filters, setFilters] = useState({ q: "", engine: "", project_type: "", clearance_min: 0 });
  const [hiring, setHiring] = useState(null);
  const [user] = useState(() => { try { return JSON.parse(localStorage.getItem("nc_user")); } catch { return null; } });

  const load = useCallback(() => {
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.engine) params.engine = filters.engine;
    if (filters.project_type) params.project_type = filters.project_type;
    if (filters.clearance_min) params.clearance_min = filters.clearance_min;
    api.get("/market/personnel", { params }).then((r) => setPersonnel(r.data)).catch(() => {});
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const requestClearance = (p) => {
    if (!user) { navigate("/auth"); return; }
    setHiring(p);
  };

  return (
    <div data-testid="gallery-page" className="min-h-screen bg-[#050505] text-zinc-100 selection:bg-nc-blue/30">
      <Seo title="Hire Game Developers — Verified Specialists" description="Hire identity-verified game developers: Unreal Engine, Unity, FiveM, Arma Reforger (Enfusion) and Minecraft specialists. Escrow-protected engagements with clearance ratings and source-complete delivery." path="/gallery" keywords="hire game developers, hire unreal engine developer, hire fivem developer, arma reforger developer hire, hire minecraft developer, verified game contractors" />
      <SiteNav />
      <MarketTabs />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
          <p className="font-mono text-[9px] tracking-[0.35em] text-nc-orange uppercase">Verified Contractors // Escrow-Protected</p>
          <h1 className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-4xl text-zinc-50 mt-2 flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-nc-blue" /> Service Gallery
          </h1>
          <p className="font-mono text-[11px] text-zinc-500 mt-2 max-w-2xl leading-relaxed">
            Every contractor is identity-verified under studio KYC protocol. Browse profiles, request a quote, and the platform holds your payment in escrow until the work is verified.
          </p>
        </motion.div>

        {/* Universal Specialist Search */}
        <div data-testid="specialist-search" className="border border-white/10 bg-black/40 mt-8 p-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="search-q" className={`${inputCls} pl-9`} placeholder="Search personnel files…" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
          </div>
          <select data-testid="search-engine" className={inputCls} value={filters.engine} onChange={(e) => setFilters((f) => ({ ...f, engine: e.target.value }))}>
            <option value="">All engines</option>
            {Object.entries(ENGINES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select data-testid="search-type" className={inputCls} value={filters.project_type} onChange={(e) => setFilters((f) => ({ ...f, project_type: e.target.value }))}>
            <option value="">All project types</option>
            {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select data-testid="search-clearance" className={inputCls} value={filters.clearance_min} onChange={(e) => setFilters((f) => ({ ...f, clearance_min: Number(e.target.value) }))}>
            <option value={0}>Any clearance</option>
            {[2, 3, 4, 5].map((l) => <option key={l} value={l}>{`Level ${l}+ clearance`}</option>)}
          </select>
        </div>

        {/* Personnel files */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
          {personnel.length === 0 && (
            <p data-testid="personnel-empty" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase col-span-full py-10 text-center">
              No personnel match this query — broaden the search parameters.
            </p>
          )}
          {personnel.map((p, i) => {
            const cl = p.clearance;
            return (
              <motion.div
                key={p.id}
                data-testid={`personnel-card-${i + 1}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.06, ease: EASE }}
                className="border border-white/10 bg-black/40 hover:border-nc-blue/40 transition-colors duration-300"
              >
                <div className="border-b border-white/10 px-4 py-2 flex items-center justify-between">
                  <span className="font-mono text-[8px] tracking-[0.3em] text-zinc-500 uppercase">Contractor // SP-{p.id.slice(0, 6).toUpperCase()}</span>
                  <span className="font-mono text-[8px] tracking-[0.2em] text-nc-green uppercase flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> Verified</span>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-sans font-bold text-lg text-zinc-50">{p.name}</p>
                      <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em] mt-0.5">{p.title || "Specialist Contractor"}</p>
                    </div>
                    <span data-testid={`clearance-${i + 1}`} className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-2 py-1 shrink-0 ${CLEARANCE_COLORS[cl.level]}`}>
                      L{cl.level} // {cl.label}
                    </span>
                  </div>
                  {p.tagline && <p className="font-mono text-[10px] text-zinc-400 mt-2 leading-relaxed">{p.tagline}</p>}
                  <div className="flex gap-1.5 flex-wrap mt-3">
                    {p.engines.map((e) => (
                      <span key={e} className={`font-mono text-[8px] tracking-[0.15em] uppercase border px-2 py-0.5 ${ENGINES[e]?.accent || "border-white/10 text-zinc-400"}`}>{ENGINES[e]?.short || e}</span>
                    ))}
                    {p.specialties.slice(0, 4).map((s) => (
                      <span key={s} className="font-mono text-[8px] tracking-[0.15em] uppercase border border-white/10 text-zinc-400 px-2 py-0.5">{s}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                    <p className="font-mono text-[10px] text-zinc-400">
                      {p.rate ? p.rate.replace(/^\$(\d+(?:\.\d+)?)(.*)$/, (_, n, s) => `${fmt(Number(n), 0)}${s}`) : "Rate on request"}
                      <span className="text-zinc-600"> // {cl.completed_engagements + cl.paid_milestones} ops</span>
                    </p>
                    <button data-testid={`request-clearance-${i + 1}`} onClick={() => requestClearance(p)} className="bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.15em] text-[9px] px-3.5 py-2 flex items-center gap-1.5 transition-colors">
                      <FileText className="w-3.5 h-3.5" /> Request Quote
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
      <SiteFooter />
      {hiring && <HireModal person={hiring} onClose={() => setHiring(null)} />}
    </div>
  );
}
